import React from 'react';
import { Zap, TrendingUp, Settings, Flame, Droplet, Leaf, LineChart } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { useNavigate } from 'react-router-dom';

// 🛡️ Defensive number coercion — see DashboardAdvancedStats.js comment.
// Critical because this widget renders on the dashboard root; a string-typed
// stat would crash the whole page.
const safeNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const DashboardMission = React.memo(function DashboardMission({
  language, t, stats, proteinProgress, effectiveCalorieTarget, calorieProgress,
  isJunior = false, isEpure = false, adaptLabel = (fr) => fr
}) {
  const navigate = useNavigate();
  const fr = language === 'fr';

  const proteinLabel = isJunior ? (fr ? 'Force' : 'Power') : t('dashboard.protein');
  const calorieLabel = isJunior ? (fr ? 'Énergie' : 'Energy') : t('dashboard.calories');
  const calorieUnit = isJunior ? 'pts' : '';

  // Carbs progress
  const carbsTarget = safeNum(stats?.carbs_target_g) || 250;
  const carbsCurrent = safeNum(stats?.today_carbs_g);
  const carbsProgress = Math.min(100, Math.round((carbsCurrent / carbsTarget) * 100));

  // Fats progress
  const fatsTarget = safeNum(stats?.fats_target_g) || 70;
  const fatsCurrent = safeNum(stats?.today_fats_g);
  const fatsProgress = Math.min(100, Math.round((fatsCurrent / fatsTarget) * 100));

  // Fiber progress
  const fiberTarget = safeNum(stats?.fiber_target) || 35;
  const fiberCurrent = safeNum(stats?.today_fiber_g);
  const fiberProgress = Math.min(100, Math.round((fiberCurrent / fiberTarget) * 100));

  return (
    <Card
      className="bg-card mb-6 relative overflow-hidden"
      data-testid="mission-du-jour"
      style={{
        border: '2px solid rgba(251,146,60,0.55)',
        boxShadow: '0 0 18px rgba(251,146,60,0.45), 0 0 6px rgba(245,158,11,0.35), inset 0 0 0 1px rgba(251,146,60,0.10)',
        background: 'linear-gradient(135deg, rgba(251,146,60,0.06), transparent 55%)',
      }}
    >
      <CardContent className={`${isEpure ? 'p-6 sm:p-8' : 'p-5 sm:p-6'}`}>
        <div className="flex items-center justify-between mb-4">
          <p
            className={`font-heading uppercase tracking-[0.2em] font-black ${isEpure ? 'text-sm' : 'text-xs'}`}
            style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.55)' }}
          >
            {fr ? 'Mission du jour' : "Today's Mission"}
          </p>
          {!isEpure && (
            <div className="flex items-center gap-1.5">
              {/* Iter. 406.fr.40 — Glow chart insights shortcut (GLOW rework 411.z) */}
              <button
                onClick={() => navigate('/macros/insights')}
                className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full transition-all active:scale-95"
                data-testid="dashboard-macros-insights-shortcut"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(251,146,60,0.22) 100%)',
                  border: '1px solid rgba(251,146,60,0.55)',
                  boxShadow: '0 0 12px rgba(251,146,60,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)',
                  color: '#fed7aa',
                  textShadow: '0 0 5px rgba(251,146,60,0.6)',
                }}
              >
                <LineChart className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.85))' }} />
                {fr ? 'Insights' : 'Insights'}
              </button>
              <button
                onClick={() => navigate('/settings', { state: { scrollTo: 'macros' } })}
                className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full transition-all active:scale-95"
                data-testid="edit-macros-shortcut"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(148,163,184,0.18) 100%)',
                  border: '1px solid rgba(148,163,184,0.5)',
                  boxShadow: '0 0 10px rgba(148,163,184,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(6px)',
                  color: '#e2e8f0',
                  textShadow: '0 0 4px rgba(148,163,184,0.55)',
                }}
              >
                <Settings className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(0 0 4px rgba(148,163,184,0.75))' }} />
                {fr ? 'Mes Objectifs' : 'My Goals'}
              </button>
            </div>
          )}
        </div>

        {/* Protein Gauge */}
        <div className="mb-5">
          <div className="flex items-end justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className={`${isEpure ? 'w-6 h-6' : 'w-5 h-5'} text-primary`} />
              <span className={`font-heading uppercase tracking-tight text-foreground ${isEpure ? 'text-lg' : 'text-base'}`}>
                {proteinLabel}
              </span>
            </div>
            <div className="text-right">
              <span className={`font-heading font-black text-primary ${isEpure ? 'text-5xl' : 'text-4xl'}`}>
                {safeNum(stats?.today_protein_g).toFixed(0)}g
              </span>
              {!isJunior && (
                <span className="text-sm text-muted-foreground ml-2">
                  / {safeNum(stats?.protein_target_min).toFixed(0)}-{safeNum(stats?.protein_target_max).toFixed(0)}g
                </span>
              )}
            </div>
          </div>
          <Progress
            value={proteinProgress}
            className={`${isEpure ? 'h-6' : 'h-5'} bg-secondary [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:via-orange-400 [&>div]:to-amber-300`}
            style={{ boxShadow: proteinProgress > 0 ? '0 0 10px rgba(251,146,60,0.55)' : 'none' }}
            data-testid="mission-protein-progress"
          />
          <p className={`text-muted-foreground mt-1.5 ${isEpure ? 'text-sm' : 'text-xs'}`}>
            {proteinProgress >= 100
              ? (fr ? 'Objectif atteint!' : 'Goal reached!')
              : (fr ? `${(100 - proteinProgress).toFixed(0)}% restant` : `${(100 - proteinProgress).toFixed(0)}% remaining`)}
          </p>
        </div>

        {/* Calories */}
        <div className={`flex items-center justify-between rounded-lg bg-secondary/50 ${isEpure ? 'p-4' : 'p-3'}`}>
          <div className="flex items-center gap-2">
            <TrendingUp className={`${isEpure ? 'w-5 h-5' : 'w-4 h-4'} text-muted-foreground`} />
            <span className={`text-muted-foreground ${isEpure ? 'text-base' : 'text-sm'}`}>{calorieLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`font-heading font-black text-foreground ${isEpure ? 'text-3xl' : 'text-2xl'}`}>
              {safeNum(stats?.today_calories).toFixed(0)}
              {calorieUnit && <span className="text-sm ml-1">{calorieUnit}</span>}
            </span>
            <span className={`text-muted-foreground ${isEpure ? 'text-base' : 'text-sm'}`}>/ {safeNum(effectiveCalorieTarget).toFixed(0)}</span>
          </div>
        </div>
        <Progress
          value={calorieProgress}
          className={`${isEpure ? 'h-3' : 'h-2'} bg-secondary mt-2 mb-5 [&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-amber-400`}
          style={{ boxShadow: calorieProgress > 0 ? '0 0 8px rgba(251,146,60,0.45)' : 'none' }}
          data-testid="mission-calorie-progress"
        />

        {/* Macros Row — Glucides, Lipides, Fibres */}
        {!isJunior && (
          <div className="grid grid-cols-3 gap-2 mt-4" data-testid="mission-macros-row">
            {/* Glucides */}
            <div className="rounded-lg bg-secondary/50 p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Flame className="w-3 h-3 text-orange-400" />
                <span className="text-[10px] font-heading uppercase tracking-tight text-muted-foreground">
                  {fr ? 'Glucides' : 'Carbs'}
                </span>
              </div>
              <p className="font-heading font-black text-foreground text-lg mb-1" data-testid="mission-carbs-value">
                {carbsCurrent.toFixed(0)}g
              </p>
              <Progress
                value={carbsProgress}
                className="h-1 bg-background [&>div]:bg-orange-400"
                data-testid="mission-carbs-progress"
              />
              <p className="text-[9px] text-muted-foreground mt-0.5">/ {carbsTarget}g</p>
            </div>

            {/* Lipides */}
            <div className="rounded-lg bg-secondary/50 p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Droplet className="w-3 h-3 text-blue-400" />
                <span className="text-[10px] font-heading uppercase tracking-tight text-muted-foreground">
                  {fr ? 'Lipides' : 'Fats'}
                </span>
              </div>
              <p className="font-heading font-black text-foreground text-lg mb-1" data-testid="mission-fats-value">
                {fatsCurrent.toFixed(0)}g
              </p>
              <Progress
                value={fatsProgress}
                className="h-1 bg-background [&>div]:bg-blue-400"
                data-testid="mission-fats-progress"
              />
              <p className="text-[9px] text-muted-foreground mt-0.5">/ {fatsTarget}g</p>
            </div>

            {/* Fibres */}
            <div className="rounded-lg bg-secondary/50 p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <Leaf className="w-3 h-3 text-green-400" />
                <span className="text-[10px] font-heading uppercase tracking-tight text-muted-foreground">
                  {fr ? 'Fibres' : 'Fiber'}
                </span>
              </div>
              <p className="font-heading font-black text-foreground text-lg mb-1" data-testid="mission-fiber-value">
                {fiberCurrent.toFixed(0)}g
              </p>
              <Progress
                value={fiberProgress}
                className="h-1 bg-background [&>div]:bg-green-400"
                data-testid="mission-fiber-progress"
              />
              <p className="text-[9px] text-muted-foreground mt-0.5">/ {fiberTarget}g</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
